import {
  PhoneOff,
  RotateCcw,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { PageSkeleton } from "@/components/page-skeleton";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useFlowGraph, useCreateCallLog } from "@/hooks/api/flows";
import { apiErrorMessage } from "@/lib/api";
import { resolveBilingual } from "@/lib/bilingual";
import { useAuthStore } from "@/stores/auth";
import type { FlowEdge, LanguagePreference, CallEndReason } from "@/types/api";

import { EndCallDialog } from "@/components/flow-player/EndCallDialog";
import { CallSummaryDisplay } from "@/components/flow-player/CallSummaryDisplay";
import { PathIndicator } from "@/components/flow-player/PathIndicator";

interface CollectedAnswer {
  question: string;
  answer: string;
}

interface WizardFlowState {
  currentNodeId: string | null;
  collectedAnswers: CollectedAnswer[];
  isComplete: boolean;
  completedAt: number | null;
}

/**
 * Wizard Flow Player Page
 *
 * Simplified, dedicated page for playing wizard-based call flows.
 * Completely isolated from existing FlowPlayerPage.
 *
 * Features:
 * - Location selector at start
 * - Answer options as clickable buttons (no typing)
 * - Path indicator showing current path
 * - Clean summary at end
 * - Copy/paste ready format
 */

function WizardFlowPlayerInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: flowData, isLoading } = useFlowGraph(id);
  const createCallLog = useCreateCallLog(id);
  const languagePreference: LanguagePreference =
    useAuthStore((s) => s.user?.language_preference) ?? "BOTH";

  const [state, setState] = useState<WizardFlowState>({
    currentNodeId: null,
    collectedAnswers: [],
    isComplete: false,
    completedAt: null,
  });

  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [endCallDialogOpen, setEndCallDialogOpen] = useState(false);
  const [callStartedAt, setCallStartedAt] = useState(() => Date.now());

  // Get all location options from greetings (nodes that start with "Greeting - ")
  const locations = useMemo(() => {
    if (!flowData?.nodes) return [];
    return flowData.nodes
      .filter((n) => n.title.startsWith("Greeting - "))
      .map((n) => ({
        id: n.id,
        name: n.title.replace("Greeting - ", ""),
        spiel_en: n.spiel_text,
        spiel_es: n.spiel_text_es,
      }));
  }, [flowData?.nodes]);

  // Get current node
  const currentNode = useMemo(
    () => flowData?.nodes.find((n) => n.id === state.currentNodeId),
    [flowData?.nodes, state.currentNodeId]
  );

  // Get edges from current node
  const nextEdges = useMemo(
    () =>
      flowData?.edges.filter((e) => e.source_node === state.currentNodeId) ?? [],
    [flowData?.edges, state.currentNodeId]
  );

  // Initialize: show location selector
  useEffect(() => {
    if (flowData && state.currentNodeId === null && locations.length > 0) {
      // Start at the first location greeting
      setSelectedLocation(locations[0].name);
      setState((s) => ({ ...s, currentNodeId: locations[0].id }));
    }
  }, [flowData, locations, state.currentNodeId]);

  // Handle location selection (greeting)
  const handleSelectLocation = (locationName: string) => {
    const location = locations.find((l) => l.name === locationName);
    if (location) {
      setSelectedLocation(locationName);
      setState((s) => ({
        ...s,
        currentNodeId: location.id,
        collectedAnswers: [],
      }));
    }
  };

  // Handle answer selection
  const handleSelectAnswer = (edge: FlowEdge, answerLabel: string) => {
    if (!currentNode) return;

    // Add answer to collected
    const newAnswer: CollectedAnswer = {
      question: currentNode.title,
      answer: answerLabel,
    };

    const newAnswers = [...state.collectedAnswers, newAnswer];

    // Navigate to next node
    const nextNode = flowData?.nodes.find((n) => n.id === edge.target_node);
    if (!nextNode) return;

    // Check if it's an END node
    if (nextNode.node_type === "END") {
      setState((s) => ({
        ...s,
        collectedAnswers: newAnswers,
        isComplete: true,
        completedAt: Date.now(),
        currentNodeId: nextNode.id,
      }));
      // Log the call
      logCall(newAnswers, "COMPLETED");
    } else {
      // Continue to next node
      setState((s) => ({
        ...s,
        collectedAnswers: newAnswers,
        currentNodeId: nextNode.id,
      }));
    }
  };

  // Handle early end call
  const handleEndCall = (input: { reason: string; note: string }) => {
    logCall(state.collectedAnswers, "ENDED_EARLY", input.reason as CallEndReason, input.note);
    setEndCallDialogOpen(false);
  };

  // Log the call
  const logCall = async (
    _answers: CollectedAnswer[],
    outcome: "COMPLETED" | "ENDED_EARLY",
    reason?: CallEndReason,
    note?: string
  ) => {
    if (!currentNode) return;

    try {
      await createCallLog.mutateAsync({
        outcome,
        started_at: new Date(callStartedAt).toISOString(),
        current_node_id: currentNode.id,
        steps: [], // Simplified - no step tracking for wizard flows
        ...(reason && { end_reason: reason }),
        ...(note && { end_reason_note: note }),
      });
      toast.success("Call logged");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to log call"));
    }
  };

  // Handle restart
  const handleRestart = () => {
    setState({
      currentNodeId: locations[0]?.id ?? null,
      collectedAnswers: [],
      isComplete: false,
      completedAt: null,
    });
    setSelectedLocation(locations[0]?.name ?? null);
    setCallStartedAt(Date.now());
  };

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (!flowData) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-600 mb-4">Flow not found</p>
        <Button onClick={() => navigate("/flows")}>Back to Flows</Button>
      </div>
    );
  }

  const flowTitle = resolveBilingual(languagePreference, {
    en: flowData.flow.title,
    es: flowData.flow.title_es,
  });

  // Step 1: Location selection
  if (!selectedLocation && locations.length > 0) {
    return (
      <div className="mx-auto w-full max-w-2xl py-8">
        <Card className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">{flowTitle.primary}</h1>
            {flowTitle.secondary && (
              <p className="text-slate-600">{flowTitle.secondary}</p>
            )}
          </div>

          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4">Select Your Location</h2>
            <div className="grid grid-cols-1 gap-3">
              {locations.map((loc) => (
                <Button
                  key={loc.id}
                  onClick={() => handleSelectLocation(loc.name)}
                  variant="secondary"
                  className="h-auto py-4 px-6 justify-start"
                >
                  <div className="text-left">
                    <div className="font-semibold text-slate-900">
                      {loc.name}
                    </div>
                    <div className="text-sm text-slate-600 mt-1">
                      {loc.spiel_en}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Step 2: Call in progress
  if (!state.isComplete) {
    // Show greeting if at greeting node
    const isGreetingNode = currentNode?.title.startsWith("Greeting - ");

    return (
      <div className="mx-auto w-full max-w-2xl py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">{flowTitle.primary}</h1>
            {flowTitle.secondary && (
              <p className="text-slate-600">{flowTitle.secondary}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEndCallDialogOpen(true)}
            >
              <PhoneOff className="h-4 w-4 mr-2" />
              End Call
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRestart}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Restart
            </Button>
          </div>
        </div>

        {/* Path Indicator */}
        {state.collectedAnswers.length > 0 && (
          <PathIndicator
            steps={state.collectedAnswers}
            currentQuestionIndex={state.collectedAnswers.length - 1}
          />
        )}

        {/* Current Node */}
        <Card className="p-8">
          {isGreetingNode ? (
            // Greeting display
            <div className="space-y-6">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-primary mb-3">
                  📞 Greeting for {selectedLocation}
                </div>
                <div className="text-2xl leading-relaxed text-slate-900">
                  {languagePreference === "ES" && currentNode?.spiel_text_es
                    ? currentNode.spiel_text_es
                    : currentNode?.spiel_text}
                </div>
                {languagePreference === "BOTH" && currentNode?.spiel_text_es && (
                  <div className="mt-4 pt-4 border-t text-slate-600">
                    <div className="text-xs text-slate-500 mb-2">Spanish:</div>
                    <div className="text-lg">
                      {currentNode.spiel_text_es}
                    </div>
                  </div>
                )}
              </div>

              {/* Proceed button after greeting */}
              {nextEdges.length > 0 && (
                <Button
                  onClick={() => {
                    const nextNode = flowData.nodes.find(
                      (n) => n.id === nextEdges[0].target_node
                    );
                    if (nextNode) {
                      setState((s) => ({
                        ...s,
                        currentNodeId: nextNode.id,
                      }));
                    }
                  }}
                  className="w-full"
                >
                  Continue →
                </Button>
              )}
            </div>
          ) : (
            // Question display
            <div className="space-y-6">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-primary mb-3">
                  ❓ Question
                </div>
                <h2 className="text-2xl font-semibold text-slate-900">
                  {languagePreference === "ES" && currentNode?.title_es
                    ? currentNode.title_es
                    : currentNode?.title}
                </h2>
              </div>

              {/* Answer buttons */}
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-3">
                  Select an answer:
                </div>
                <div className="flex flex-col gap-2">
                  {nextEdges.map((edge, idx) => (
                    <Button
                      key={edge.id}
                      onClick={() =>
                        handleSelectAnswer(
                          edge,
                          languagePreference === "ES" && edge.label_es
                            ? edge.label_es
                            : edge.label
                        )
                      }
                      variant="secondary"
                      className="justify-start h-auto py-3 px-4 text-left"
                    >
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900">
                          {String.fromCharCode(97 + idx)}) {edge.label}
                        </div>
                        {languagePreference === "BOTH" && edge.label_es && (
                          <div className="text-xs text-slate-600 mt-1">
                            {edge.label_es}
                          </div>
                        )}
                        {languagePreference === "ES" && edge.label_es && (
                          <div className="text-slate-900">{edge.label_es}</div>
                        )}
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* End Call Dialog */}
        <EndCallDialog
          open={endCallDialogOpen}
          onClose={() => setEndCallDialogOpen(false)}
          onConfirm={handleEndCall}
          submitting={createCallLog.isPending}
        />
      </div>
    );
  }

  // Step 3: Call complete - show summary
  return (
    <div className="mx-auto w-full max-w-2xl py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Call Complete</h1>
        <Button variant="secondary" size="sm" onClick={handleRestart}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Start New Call
        </Button>
      </div>

      {/* Summary */}
      <CallSummaryDisplay
        flowTitle={flowData.flow.title}
        completedAt={state.completedAt || Date.now()}
        entries={state.collectedAnswers}
        outcome="Call Completed"
        onDismiss={handleRestart}
      />

      {/* Outcomes */}
      <Card className="mt-6 p-6">
        <h3 className="font-semibold mb-4">What's Next?</h3>
        <div className="flex flex-col gap-3">
          <Button variant="secondary">📞 Transfer Call</Button>
          <Button variant="secondary">📅 Schedule Callback</Button>
          <Button variant="secondary">🔄 Reschedule Callback</Button>
          <Button onClick={handleRestart}>Start New Call</Button>
        </div>
      </Card>
    </div>
  );
}

export default function WizardFlowPlayerPage() {
  return (
    <AppShell>
      <WizardFlowPlayerInner />
    </AppShell>
  );
}
